import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client'
import type { GetServerSidePropsContext } from 'next/types'

import { env, log } from '@gaia-family/lib'
import { onError } from '@apollo/client/link/error'

import { AUTH_COOKIE_TOKEN, SECURE_AUTH_COOKIE_TOKEN } from '@lib/auth'

// Catch-all for network errors
// Does not swallow error here
const errorLink = onError(({ networkError }) => {
  if (networkError)
    log.error(networkError, `[Network error]: ${networkError.message}`)
})

const browserHttpLink = new HttpLink({ uri: `/graphql` })

/**
 * Create a new apollo client instance
 *
 * @see createServerApolloClient
 */
export const createBrowserApolloClient = () => {
  log.trace({ ns: 'app:www$lib:apollo', name: 'create-client' })

  return new ApolloClient({
    cache: new InMemoryCache(),
    // errorLink doesnt play nice unless with httpLink
    link: from([errorLink, browserHttpLink]),
  })
}

/**
 * Create a new apollo client instance, optionally with some request
 * context
 *
 * If we're running within the server process we can directly query our
 * backend graphql endpoint, otherwise we need to proxy it through our
 * local /graphql route (see _middleware.ts which does an auth rewrite)
 *
 * @see createBrowserApolloClient
 */
export const createServerApolloClient = (
  req?: GetServerSidePropsContext['req']
) => {
  // try and find an auth token from the request cookie
  const cookieAuthToken =
    req?.cookies[SECURE_AUTH_COOKIE_TOKEN] ?? req?.cookies[AUTH_COOKIE_TOKEN]

  log.trace(
    { ns: 'app:www$lib:apollo', name: 'create-client' },
    `hasAuthToken: %s`,
    !!cookieAuthToken
  )

  const headers = cookieAuthToken
    ? {
        Authorization: `Bearer ${cookieAuthToken}`,
      }
    : undefined

  // create a direct http link to our backend
  return new ApolloClient({
    link: new HttpLink({
      uri: `${env.GAIA_SERVICE_GRAPHQL_HTTP_URL}/graphql`,
      headers,
    }),
    ssrMode: true,
    cache: new InMemoryCache(),
    ssrForceFetchDelay: 100, // in milliseconds
  })
}
